import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { PaginationBar } from "@/components/ui/PaginationBar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { orderDiscountApprovalsApi, type OrderDiscountRequest, type OrderDiscountStatus } from "../services/orderDiscountApprovalsApi";

const money = (value: number) => `KES ${value.toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (value: string | null) => value ? new Date(value).toLocaleString("en-KE") : "—";

export function DiscountApprovalsPage() {
  const [status, setStatus] = useState<OrderDiscountStatus>("PendingApproval");
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selected, setSelected] = useState<OrderDiscountRequest | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const { hasPermission } = useAuthStore();
  const client = useQueryClient();
  const queue = useQuery({
    queryKey: ["discount-approvals", status, search, pageNumber, pageSize],
    queryFn: () => orderDiscountApprovalsApi.list(status, search, pageNumber, pageSize),
    placeholderData: previous => previous,
  });
  const detail = useQuery({
    queryKey: ["discount-approval", selected?.id],
    queryFn: () => orderDiscountApprovalsApi.detail(selected!.id),
    enabled: !!selected,
  });
  const decision = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "approve" | "reject" }) =>
      orderDiscountApprovalsApi.decide(id, action, rejectionReason),
    onSuccess: async (_, variables) => {
      toast.success(variables.action === "approve" ? "Discount approved" : "Discount rejected");
      setSelected(null);
      setRejectionReason("");
      await client.invalidateQueries({ queryKey: ["discount-approvals"] });
    },
    onError: () => toast.error("Review failed. Refresh the request and try again."),
  });
  const rows = queue.data?.items ?? [];
  const current = detail.data ?? selected;
  const canApprove = hasPermission("discounts.approve_order");
  return <div className="space-y-5 p-6">
    <div><h1 className="text-2xl font-semibold">Discount Approvals</h1><p className="text-sm text-muted-foreground">Review negotiated order prices and their financial impact.</p></div>
    <div className="flex flex-wrap gap-2">
      {(["PendingApproval", "Approved", "Rejected"] as OrderDiscountStatus[]).map(value =>
        <Button key={value} variant={status === value ? "default" : "outline"} onClick={() => { setStatus(value); setPageNumber(1); }}>
          {value === "PendingApproval" ? "Pending" : value}
        </Button>)}
      <Input aria-label="Search customer or order" placeholder="Search customer or order" className="max-w-sm" value={search}
        onChange={event => { setSearch(event.target.value); setPageNumber(1); }} />
    </div>
    <Table><TableHeader><TableRow>
      <TableHead>Order</TableHead><TableHead>Customer</TableHead><TableHead>Requested</TableHead>
      <TableHead>Existing discount</TableHead><TableHead>Additional reduction</TableHead>
      <TableHead>Requester</TableHead><TableHead>Submitted</TableHead><TableHead>Status</TableHead><TableHead />
    </TableRow></TableHeader><TableBody>
      {rows.map(row => <TableRow key={row.id}>
        <TableCell>{row.orderNumber}</TableCell><TableCell>{row.customerName}</TableCell>
        <TableCell>{row.kind === "Percentage" ? `${row.value}%` : money(row.value)}</TableCell>
        <TableCell>{money(row.existingDiscountAmount)}</TableCell><TableCell>{money(row.incrementalReduction)}</TableCell>
        <TableCell>{row.requesterName}</TableCell><TableCell>{date(row.submittedAt)}</TableCell>
        <TableCell><Badge variant="outline">{row.status}</Badge></TableCell>
        <TableCell><Button size="sm" variant="outline" onClick={() => { setSelected(row); setRejectionReason(""); }}>Review</Button></TableCell>
      </TableRow>)}
      {rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground">{queue.isLoading ? "Loading…" : "No requests found"}</TableCell></TableRow>}
    </TableBody></Table>
    <PaginationBar currentPage={pageNumber} pageSize={pageSize} totalCount={queue.data?.totalCount ?? 0}
      totalPages={Math.max(1, Math.ceil((queue.data?.totalCount ?? 0) / pageSize))}
      onPageChange={setPageNumber} onPageSizeChange={size => { setPageSize(size); setPageNumber(1); }} />
    <Dialog open={!!selected} onOpenChange={open => { if (!open) setSelected(null); }}>
      <DialogContent className="max-w-xl"><DialogHeader><DialogTitle>Review special discount</DialogTitle>
        <DialogDescription>{current?.orderNumber} · {current?.customerName}</DialogDescription></DialogHeader>
        {current && <div className="space-y-2 text-sm">
          <p><strong>Reason:</strong> {current.reason}</p>
          <p><strong>Existing discount:</strong> {money(current.existingDiscountAmount)} {current.existingDiscountReference ? `(${current.existingDiscountReference})` : ""}</p>
          <p><strong>Requested:</strong> {current.kind === "Percentage" ? `${current.value}%` : money(current.value)}</p>
          {current.kind === "FixedAmount" && current.goodsSubtotal > 0 &&
            <p><strong>Equivalent rate:</strong> {(current.value * 100 / current.goodsSubtotal).toFixed(2)}%</p>}
          <div className="grid grid-cols-2 gap-2 rounded-md bg-muted p-3">
            <span>Original goods subtotal</span><strong>{money(current.goodsSubtotal)}</strong>
            <span>Current payable goods</span><strong>{money(current.currentGoodsPayable)}</strong>
            <span>Proposed payable goods</span><strong>{money(current.proposedGoodsPayable)}</strong>
            <span>Additional reduction</span><strong>{money(current.incrementalReduction)}</strong>
          </div>
          {current.rejectionReason && <p><strong>Rejection reason:</strong> {current.rejectionReason}</p>}
          {current.status === "PendingApproval" && canApprove && <Textarea aria-label="Rejection reason" placeholder="Reason required to reject" value={rejectionReason} onChange={event => setRejectionReason(event.target.value)} />}
        </div>}
        <DialogFooter>
          {current?.status === "PendingApproval" && canApprove && <>
            <Button variant="destructive" disabled={decision.isPending || !rejectionReason.trim()} onClick={() => decision.mutate({ id: current.id, action: "reject" })}>Reject</Button>
            <Button disabled={decision.isPending} onClick={() => decision.mutate({ id: current.id, action: "approve" })}>Approve</Button>
          </>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>;
}
